import { useEffect, useState } from 'react';
import apiClient from '@/lib/api.ts';

// ─── Types ───────────────────────────────────────────────────────────────────

type TenantEstado = 'active' | 'trial' | 'suspended';

interface Tenant {
  id: string;
  nombre_comercial: string;
  razon_social: string;
  cif_nif: string;
  dominio_slug: string;
  estado: TenantEstado;
  created_at: string;
}

interface CreateTenantForm {
  nombre_comercial: string;
  razon_social: string;
  cif_nif: string;
  dominio_slug: string;
  estado: TenantEstado;
}

const EMPTY_FORM: CreateTenantForm = {
  nombre_comercial: '',
  razon_social: '',
  cif_nif: '',
  dominio_slug: '',
  estado: 'trial',
};

const ESTADOS: TenantEstado[] = ['active', 'trial', 'suspended'];

const ESTADO_LABELS: Record<TenantEstado, string> = {
  active:    'Activo',
  trial:     'Trial',
  suspended: 'Suspendido',
};

const ESTADO_BADGE: Record<TenantEstado, { background: string; color: string }> = {
  active:    { background: 'rgba(34,197,94,0.12)',  color: '#22c55e' },
  trial:     { background: 'rgba(245,158,11,0.12)', color: '#f59e0b' },
  suspended: { background: 'rgba(239,68,68,0.12)',  color: '#ef4444' },
};

// Paleta — apunta a los tokens CSS del tema global (index.css)
const C = {
  bg:      'var(--color-bg)',
  surface: 'var(--color-surface)',
  border:  'var(--color-border)',
  text:    'var(--color-text)',
  muted:   'var(--color-muted)',
  primary: 'var(--color-accent)',
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 500, color: C.muted, }}>{label}</label>
      {children}
    </div>
  );
}

function Input(props: React.InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      style={{
        padding: '8px 12px',
        borderRadius: 6,
        fontSize: 14,
        background: C.bg,
        border: `1px solid ${C.border}`,
        color: C.text,
       
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
      }}
    />
  );
}

function Select(props: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select
      {...props}
      style={{
        padding: '8px 12px',
        borderRadius: 6,
        fontSize: 14,
        background: C.bg,
        border: `1px solid ${C.border}`,
        color: C.text,
       
        outline: 'none',
        cursor: 'pointer',
      }}
    />
  );
}

function Btn({
  children, onClick, variant = 'ghost', disabled, type = 'button',
}: {
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'subtle';
  disabled?: boolean;
  type?: 'button' | 'submit';
}) {
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: C.primary, color: '#fff' },
    ghost:   { background: C.border,  color: C.text  },
    subtle:  { background: 'transparent', color: C.muted, border: `1px solid ${C.border}` },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: '8px 16px',
        borderRadius: 6,
        fontSize: 14,
        fontWeight: 500,
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1,
       
        ...styles[variant],
      }}
    >
      {children}
    </button>
  );
}

function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: 'rgba(0,0,0,0.6)' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div style={{ width: '100%', maxWidth: 480, borderRadius: 12, padding: 24, background: C.surface, border: `1px solid ${C.border}`, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 style={{ fontSize: 16, fontWeight: 600, color: C.text, margin: 0 }}>{title}</h2>
          <button onClick={onClose} style={{ fontSize: 18, color: C.muted, background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }} aria-label="Cerrar">×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function TenantsPage() {
  const [tenants, setTenants]     = useState<Tenant[]>([]);
  const [total, setTotal]         = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [search, setSearch]       = useState('');
  const [filterEstado, setFilterEstado] = useState<TenantEstado | ''>('');

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm]             = useState<CreateTenantForm>(EMPTY_FORM);
  const [saving, setSaving]         = useState(false);
  const [formError, setFormError]   = useState<string | null>(null);

  const [editingTenant, setEditingTenant]   = useState<Tenant | null>(null);
  const [newEstado, setNewEstado]           = useState<TenantEstado>('active');
  const [updatingEstado, setUpdatingEstado] = useState(false);

  async function loadTenants() {
    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams({ limit: '100' });
      if (filterEstado) params.set('estado', filterEstado);
      if (search.trim()) params.set('search', search.trim());
      const res = await apiClient.get<{ data: Tenant[]; total: number }>(`/admin/tenants?${params}`);
      setTenants(res.data.data ?? []);
      setTotal(res.data.total ?? 0);
    } catch {
      setError('No se pudieron cargar los tenants.');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => { loadTenants(); }, [filterEstado]);

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setFormError(null);
    try {
      await apiClient.post('/admin/tenants', form);
      setShowCreate(false);
      setForm(EMPTY_FORM);
      await loadTenants();
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Error al crear el tenant.';
      setFormError(msg);
    } finally {
      setSaving(false);
    }
  }

  async function handleUpdateEstado() {
    if (!editingTenant) return;
    setUpdatingEstado(true);
    try {
      await apiClient.patch(`/admin/tenants/${editingTenant.id}/estado`, { estado: newEstado });
      setEditingTenant(null);
      await loadTenants();
    } finally {
      setUpdatingEstado(false);
    }
  }

  const COLS = ['Empresa', 'Razon social', 'CIF / NIF', 'Slug', 'Estado', 'Alta', 'Acciones'];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24, }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: C.text, margin: 0 }}>Gestion de Tenants</h1>
          <p style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>
            {total} empresa{total !== 1 ? 's' : ''} registrada{total !== 1 ? 's' : ''}
          </p>
        </div>
        <Btn variant="primary" onClick={() => { setShowCreate(true); setForm(EMPTY_FORM); setFormError(null); }}>
          + Nuevo tenant
        </Btn>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
        <form
          onSubmit={e => { e.preventDefault(); loadTenants(); }}
          style={{ display: 'flex', gap: 8, flex: 1, minWidth: 240 }}
        >
          <Input
            type="text"
            placeholder="Buscar por nombre o slug..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <Btn type="submit" variant="ghost">Buscar</Btn>
        </form>
        <Select value={filterEstado} onChange={e => setFilterEstado(e.target.value as TenantEstado | '')}>
          <option value="">Todos los estados</option>
          {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABELS[e]}</option>)}
        </Select>
      </div>

      {/* Error */}
      {error && (
        <div style={{ padding: '12px 16px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: 14 }}>
          {error}
        </div>
      )}

      {/* Tabla */}
      <div style={{ borderRadius: 10, overflow: 'hidden', border: `1px solid ${C.border}` }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14, }}>
          <thead>
            <tr style={{ borderBottom: `1px solid ${C.border}` }}>
              {COLS.map(h => (
                <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 500, fontSize: 12, color: C.muted, background: C.surface, whiteSpace: 'nowrap' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <tr key={i} style={{ borderBottom: `1px solid ${C.border}` }}>
                  {COLS.map((_, j) => (
                    <td key={j} style={{ padding: '14px 16px' }}>
                      <div style={{ height: 14, borderRadius: 4, background: C.border, width: '70%' }} />
                    </td>
                  ))}
                </tr>
              ))
            ) : tenants.length === 0 ? (
              <tr>
                <td colSpan={COLS.length} style={{ padding: '40px 16px', textAlign: 'center', color: C.muted }}>
                  No se encontraron tenants.
                </td>
              </tr>
            ) : tenants.map(t => (
              <tr key={t.id} style={{ borderBottom: `1px solid ${C.border}`, background: C.surface }}>
                <td style={{ padding: '14px 16px', fontWeight: 500, color: C.text, whiteSpace: 'nowrap' }}>{t.nombre_comercial}</td>
                <td style={{ padding: '14px 16px', color: C.muted }}>{t.razon_social}</td>
                <td style={{ padding: '14px 16px', color: C.muted, fontSize: 12 }}>{t.cif_nif}</td>
                <td style={{ padding: '14px 16px', color: C.muted, fontSize: 12 }}>{t.dominio_slug}</td>
                <td style={{ padding: '14px 16px' }}>
                  <span style={{ ...ESTADO_BADGE[t.estado], padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 500 }}>
                    {ESTADO_LABELS[t.estado]}
                  </span>
                </td>
                <td style={{ padding: '14px 16px', color: C.muted, whiteSpace: 'nowrap' }}>
                  {new Date(t.created_at).toLocaleDateString('es-ES')}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <button
                    onClick={() => { setEditingTenant(t); setNewEstado(t.estado); }}
                    style={{ padding: '5px 10px', borderRadius: 5, fontSize: 12, background: C.border, color: C.text, border: 'none', cursor: 'pointer', }}
                  >
                    Cambiar estado
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal crear */}
      {showCreate && (
        <Modal title="Nuevo tenant" onClose={() => setShowCreate(false)}>
          <form onSubmit={handleCreate} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {formError && (
              <p style={{ margin: 0, padding: '10px 12px', borderRadius: 6, background: '#fef2f2', color: '#dc2626', fontSize: 13 }}>{formError}</p>
            )}
            {([
              { field: 'nombre_comercial', label: 'Nombre comercial' },
              { field: 'razon_social',     label: 'Razon social' },
              { field: 'cif_nif',          label: 'CIF / NIF' },
              { field: 'dominio_slug',      label: 'Slug (subdominio)' },
            ] as { field: keyof CreateTenantForm; label: string }[]).map(({ field, label }) => (
              <Field key={field} label={label}>
                <Input required value={form[field] as string} onChange={e => setForm(f => ({ ...f, [field]: e.target.value }))} />
              </Field>
            ))}
            <Field label="Estado inicial">
              <Select value={form.estado} onChange={e => setForm(f => ({ ...f, estado: e.target.value as TenantEstado }))}>
                {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABELS[e]}</option>)}
              </Select>
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, paddingTop: 4 }}>
              <Btn variant="ghost" onClick={() => setShowCreate(false)}>Cancelar</Btn>
              <Btn variant="primary" type="submit" disabled={saving}>{saving ? 'Guardando...' : 'Crear tenant'}</Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* Modal cambiar estado */}
      {editingTenant && (
        <Modal title={`Estado — ${editingTenant.nombre_comercial}`} onClose={() => setEditingTenant(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <Field label="Nuevo estado">
              <Select value={newEstado} onChange={e => setNewEstado(e.target.value as TenantEstado)}>
                {ESTADOS.map(e => <option key={e} value={e}>{ESTADO_LABELS[e]}</option>)}
              </Select>
            </Field>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <Btn variant="ghost" onClick={() => setEditingTenant(null)}>Cancelar</Btn>
              <Btn variant="primary" onClick={handleUpdateEstado} disabled={updatingEstado}>{updatingEstado ? 'Guardando...' : 'Confirmar'}</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
